import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon, CalendarIcon } from "@heroicons/react/24/outline";
import { AssessmentSlots, InterviewSlots } from "../../Models";
import useApiToken from "../../hooks/useApiToken";
import { Spinner } from "../Shared/Spinner";
import { useNotification } from "../../context/NotificationContext";
import {
  getAssessmentSlotsByRole,
  GetAvailableInterviewSlots,
} from "../../api/roles.api";
import {
  BookAssessmentSlot,
  BookInterviewSlot,
} from "../../api/candidates.api";

interface BookInterviewSlotProps {
  isOpen: boolean;
  onClose: () => void;
  candidateId: string;
  roleId: string;
  onSlotBooked?: () => void;
}

type TabType = "interviews" | "assessments";

export default function BookInterviewSlotModal({
  isOpen,
  onClose,
  candidateId,
  roleId,
  onSlotBooked,
}: BookInterviewSlotProps) {
  const [activeTab, setActiveTab] = useState<TabType>("interviews");
  const [availableInterviewSlots, setAvailableInterviewSlots] = useState<
    InterviewSlots[]
  >([]);
  const [availableAssessmentSlots, setAvailableAssessmentSlots] = useState<
    AssessmentSlots[]
  >([]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const { success, error: notifyError } = useNotification();
  const token = useApiToken();

  useEffect(() => {
    if (isOpen && token) {
      fetchavailableInterviewSlots();
      fetchavailableAssessmentSlots();
    }
  }, [isOpen, token, roleId]);

  // Reset selected slot when switching tabs
  useEffect(() => {
    setSelectedSlot(null);
  }, [activeTab]);

  const fetchavailableInterviewSlots = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const slots = await GetAvailableInterviewSlots(token, roleId);
      setAvailableInterviewSlots(slots);
    } catch (error: any) {
      console.error("Error fetching available slots:", error);
      const errorMessage =
        error.message ||
        "Failed to fetch available slots. Please ensure the backend API is running and the endpoint is available.";
      notifyError("Error", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchavailableAssessmentSlots = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const slots = await getAssessmentSlotsByRole(roleId,token);
      const futureSlots = slots.filter(
        (slot: AssessmentSlots) => new Date(slot.startTime) > new Date(),
      );
      setAvailableAssessmentSlots(futureSlots);
    } catch (error: any) {
      console.error("Error fetching available slots:", error);
      const errorMessage =
        error.message ||
        "Failed to fetch available slots. Please ensure the backend API is running and the endpoint is available.";
      notifyError("Error", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookSlot = async () => {
    if (!selectedSlot || !token) return;

    setIsBooking(true);
    try {
      if (activeTab === "assessments") {
        await BookAssessmentSlot(
          selectedSlot,
          candidateId,
          notes || null,
          token,
        );
        success("Success", "Assessment slot booked successfully");
      } else {
        await BookInterviewSlot(
          selectedSlot,
          candidateId,
          notes || null,
          token,
        );
        success("Success", "Interview slot booked successfully");
      }

      onSlotBooked?.();
      handleClose();
    } catch (error: any) {
      notifyError("Error", error.message || "Failed to book slot");
    } finally {
      setIsBooking(false);
    }
  };

  const handleClose = () => {
    setSelectedSlot(null);
    setNotes("");
    setActiveTab("interviews");
    onClose();
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const currentSlots =
    activeTab === "interviews"
      ? availableInterviewSlots
      : availableAssessmentSlots;
  const noSlotsMessage =
    activeTab === "interviews"
      ? "No available interview slots found for this role."
      : "No available assessment slots found for this role.";

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-darkbglight px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                  <div className="absolute right-0 top-0 pr-4 pt-4">
                    <button
                      type="button"
                      className="rounded-md bg-white dark:bg-darkbglight text-gray-400 hover:text-gray-500"
                      onClick={handleClose}
                    >
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <CalendarIcon
                        className="h-6 w-6 text-blue-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-300"
                      >
                        Book Slot
                      </Dialog.Title>

                      {/* Tabs */}
                      <div className="mt-4 border-b border-gray-200 dark:border-gray-600">
                        <nav className="-mb-px flex space-x-8">
                          <button
                            onClick={() => setActiveTab("interviews")}
                            className={`${
                              activeTab === "interviews"
                                ? "border-entntblue text-entntblue"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors`}
                          >
                            Interviews
                          </button>
                          <button
                            onClick={() => setActiveTab("assessments")}
                            className={`${
                              activeTab === "assessments"
                                ? "border-entntblue text-entntblue"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors`}
                          >
                            Assessments
                          </button>
                        </nav>
                      </div>

                      <div className="mt-4">
                        {isLoading ? (
                          <div className="flex justify-center py-8">
                            <Spinner />
                          </div>
                        ) : currentSlots.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {noSlotsMessage}
                          </p>
                        ) : (
                          <>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Select{" "}
                              {activeTab === "interviews"
                                ? "Interview"
                                : "Assessment"}{" "}
                              Slot
                            </label>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {currentSlots.map((slot) => (
                                <div
                                  key={slot.id}
                                  className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                                    selectedSlot === slot.id
                                      ? "border-entntblue bg-blue-50 dark:bg-blue-900/20"
                                      : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
                                  }`}
                                  onClick={() => setSelectedSlot(slot.id)}
                                >
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-300">
                                        {slot.roleStatus?.name ||
                                          (activeTab === "interviews"
                                            ? "Interview"
                                            : "Assessment")}
                                      </p>
                                      <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {formatDateTime(slot.startTime)} -{" "}
                                        {new Date(
                                          slot.endTime,
                                        ).toLocaleTimeString("en-US", {
                                          timeStyle: "short",
                                        })}
                                      </p>
                                      {activeTab === "interviews" &&
                                        (slot as InterviewSlots)
                                          .interviewerEmail && (
                                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            Interviewer:{" "}
                                            {
                                              (slot as InterviewSlots)
                                                .interviewerEmail
                                            }
                                          </p>
                                        )}
                                    </div>
                                    <input
                                      type="radio"
                                      checked={selectedSlot === slot.id}
                                      onChange={() => setSelectedSlot(slot.id)}
                                      className="h-4 w-4 text-entntblue focus:ring-entntblue"
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>

                            <div className="mt-4">
                              <label
                                htmlFor="notes"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                              >
                                Notes (Optional)
                              </label>
                              <textarea
                                id="notes"
                                rows={3}
                                className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-darkbg dark:text-gray-300 shadow-sm focus:border-entntblue focus:ring-entntblue sm:text-sm"
                                placeholder="Add any notes for the candidate..."
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-entntblue px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-entntorange sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleBookSlot}
                      disabled={!selectedSlot || isBooking}
                    >
                      {isBooking ? (
                        <>
                          <Spinner />
                          <span className="ml-2">Booking...</span>
                        </>
                      ) : (
                        "Book Slot"
                      )}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-darkbg px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-300 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 sm:mt-0 sm:w-auto"
                      onClick={handleClose}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
